<script setup lang="ts">
    import { computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { longDate } from '@/formatters.ts';
    import type { ReleaseSearchResult } from '@/shared/helpers/api';
    import { getReleaseLink } from '@/shared/helpers/routing';
    import { statusGroupForState, specificationTypeLabel } from './useReleaseSearchFilters';

    type ReleaseRow = ReleaseSearchResult & {
        typeLabel: string;
        visibleScopes: string[];
        hiddenScopeCount: number;
        hiddenScopes: string;
    };

    /** A release can carry well over a hundred scopes, which would dwarf every other row. */
    const MAX_VISIBLE_SCOPES = 5;
    const router = useRouter();

    const props = withDefaults(defineProps<{
        releases: ReleaseSearchResult[];
        loading?: boolean;
        emptyText?: string;
    }>(), {
        loading: false,
        emptyText: 'No releases match the current filters'
    });

    // Buefy sorts and searches on the raw cell value, so the type label is
    // precomputed to keep that column behaving like the text the user sees.
    const rows = computed<ReleaseRow[]>(() => props.releases.map(r => ({
        ...r,
        typeLabel: specificationTypeLabel(r.specificationType),
        visibleScopes: r.scopes.slice(0, MAX_VISIBLE_SCOPES),
        hiddenScopeCount: Math.max(r.scopes.length - MAX_VISIBLE_SCOPES, 0),
        hiddenScopes: r.scopes.slice(MAX_VISIBLE_SCOPES).join(', ')
    })));

    // The id is a number so that it sorts as one; searching it still has to work
    // on the digits the user types.
    function searchReleaseId(row: ReleaseRow, input: string): boolean {
        return `${row.id}`.includes(input);
    }

    async function openRelease(row: ReleaseRow): Promise<void> {
        await router.push(getReleaseLink(row.id));
    }
</script>

<template>
    <b-table
        class="mt-2"
        :data="rows"
        :loading="loading"
        :paginated="true"
        :per-page="20"
        striped
        narrowed
        hoverable
        @click="openRelease">
        <b-table-column
            v-slot="tableProps"
            field="state"
            label="Status"
            sortable>
            <b-tooltip
                :label="tableProps.row.state"
                position="is-right">
                <b-icon
                    :icon="statusGroupForState(tableProps.row.state).icon"
                    :class="statusGroupForState(tableProps.row.state).colorClass"
                    size="is-small" />
            </b-tooltip>
            <b-tooltip
                v-if="tableProps.row.hasErrors"
                label="This release has errors"
                position="is-right">
                <b-icon
                    icon="alert"
                    class="has-text-danger"
                    size="is-small" />
            </b-tooltip>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="id"
            label="Release"
            width="100"
            sortable
            searchable
            :custom-search="searchReleaseId">
            <router-link
                :to="getReleaseLink(tableProps.row.id)"
                class="release-link"
                @click.stop>
                {{ tableProps.row.id }}
            </router-link>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="typeLabel"
            label="Type"
            sortable>
            <span class="is-oneline">{{ tableProps.row.typeLabel }}</span>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="libraries"
            label="Library"
            sortable
            searchable>
            <span
                v-for="(library, idx) in tableProps.row.libraries"
                :key="library">
                <span v-if="idx !== 0">, </span>
                {{ library }}
            </span>
            <b v-if="tableProps.row.libraries.length === 0">None</b>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            label="Scopes">
            <div class="tags">
                <span
                    v-for="scope in tableProps.row.visibleScopes"
                    :key="scope"
                    class="tag is-small is-success">
                    {{ scope }}
                </span>
                <b-tooltip
                    v-if="tableProps.row.hiddenScopeCount > 0"
                    position="is-left"
                    multilined
                    :label="tableProps.row.hiddenScopes">
                    <span class="tag is-small">+{{ tableProps.row.hiddenScopeCount }} more</span>
                </b-tooltip>
            </div>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="dateCreated"
            label="Created (UTC)"
            sortable>
            <span class="is-oneline">{{ longDate(tableProps.row.dateCreated) }}</span>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="dateUpdated"
            label="Updated (UTC)"
            sortable>
            <span class="is-oneline">{{ longDate(tableProps.row.dateUpdated) }}</span>
        </b-table-column>
        <b-table-column
            v-slot="tableProps"
            field="createdByName"
            label="Created by"
            sortable
            searchable>
            <b-tooltip
                v-if="tableProps.row.createdByName && tableProps.row.createdByEmail"
                position="is-left"
                :label="tableProps.row.createdByEmail">
                <span class="is-oneline">{{ tableProps.row.createdByName }}</span>
            </b-tooltip>
            <div v-else>
                {{ tableProps.row.createdByName ?? 'Unknown user' }}
            </div>
        </b-table-column>

        <template #empty>
            <section class="section">
                <div class="content has-text-grey has-text-centered">
                    <p>{{ emptyText }}</p>
                </div>
            </section>
        </template>
    </b-table>
</template>

<style scoped>
  :deep(.table tbody tr) {
    cursor: pointer;
  }

  .release-link {
    color: inherit;
    text-decoration: none;
  }

  .release-link:hover,
  .release-link:focus-visible {
    color: var(--eq-norwegian-woods-1);
    text-decoration: underline;
  }
</style>
